/**
 * The guest's liveness ping, wired to this app (card `t_6d25f68a`, seam 3).
 *
 * The loop itself is `services/demoGuestHeartbeat.ts`; this is the part that
 * knows what a ping is *here*: the app's own hub fetch (`apiFetch` — it carries
 * the connection's credential, so a guest's own JWT goes out) and what "a guest
 * is here" means (`useDemoGuest`: the welcome page's flag until `/users/me`
 * answers, the hub role `viewer` after it — the same two facts the guest chrome
 * reads).
 *
 * `skipAuthRefresh: true`: a liveness ping must not spend the 401 machinery
 * (the silent token refresh, `notifySessionExpired`). The loop's own answer to
 * a refusal is to stop, and a session that really died is reported by whatever
 * the person does next, through the normal path.
 *
 * Mounted once from the app root (`App.vue`). A stop is final for the document
 * — a session the hub has already dropped does not come back by pinging it.
 */
import { watch } from 'vue';
import { apiFetch } from '../services/api';
import { classifyGuestHeartbeatAnswer, startGuestHeartbeat } from '../services/demoGuestHeartbeat';
import { errorStatus } from '../utils/errors';
import { useDemoGuest } from './useDemoGuest';

export function useDemoGuestHeartbeat(): void {
  const { isGuest } = useDemoGuest();
  let stop: (() => void) | null = null;

  watch(
    isGuest,
    (guest) => {
      if (!guest || stop) return;
      stop = startGuestHeartbeat({
        isGuest: () => isGuest.value,
        ping: async () => {
          try {
            await apiFetch('/demo/session/heartbeat', { method: 'POST', skipAuthRefresh: true });
            return classifyGuestHeartbeatAnswer(204);
          } catch (error) {
            // No status at all (offline, DNS) classifies as `retry`.
            return classifyGuestHeartbeatAnswer(errorStatus(error) ?? 0);
          }
        },
      });
    },
    { immediate: true },
  );
}
